import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Dialog } from '../../../../ui';
import { DIALOG_TYPE } from '../../../../constants';
import { Pencil, Plus, Trash2 } from 'lucide-react';

export interface OrganizationUnit {
  id: string;
  unitName: string;
  organizationId: string;
  parentOrganizationUnitId: string;
  children: OrganizationUnit[];
  isDirty?: boolean;
  isDeleted?: boolean;
}

interface FolderProps {
  organizationUnit: OrganizationUnit;
  onDragStart?: (folder: OrganizationUnit) => void;
  onDragEnter?: (folder: OrganizationUnit) => void;
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (
    event: React.DragEvent<HTMLDivElement>,
    folder: OrganizationUnit
  ) => void;
  onEditClick: (folder: OrganizationUnit) => void;
  onDeleteClick: (folder: OrganizationUnit) => void;
  onAddClick: (folder: OrganizationUnit) => void;
  dragTarget: OrganizationUnit | null;
}

export const TreeStructure: React.FC<FolderProps> = ({
  organizationUnit,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  onEditClick,
  onDeleteClick,
  onAddClick,
  dragTarget,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const folderRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(organizationUnit);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(organizationUnit);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event, organizationUnit);
    }
  };

  const onEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onEditClick(organizationUnit);
  };

  const onDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setShowModal(true);
  };

  const onAdd = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onAddClick(organizationUnit);
  };

  const isDragTarget = dragTarget && dragTarget.id === organizationUnit.id;

  const confirmDelete = () => {
    onDeleteClick(organizationUnit);
    setShowModal(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        ref={folderRef}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        variant="outlined"
        sx={{
          borderRadius: '8px',
          bgcolor: isExpanded ? 'action.hover' : 'background.paper',
          borderColor: isDragTarget ? 'primary.main' : 'divider',
          my: 0.5,
          transition: 'all 0.2s ease',
          boxShadow: isDragTarget ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
          '&:hover': {
            borderColor: 'action.active',
            bgcolor: 'action.hover',
          },
        }}
      >
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(90deg)' : 'none',
                color: 'text.secondary',
              }}
            >
              <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
              {organizationUnit.unitName}
            </Typography>
            {organizationUnit.isDirty && (
              <Chip
                size="small"
                label="Unsaved"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem', ml: 1 }}
              />
            )}
            {organizationUnit.isDeleted && (
              <Chip
                size="small"
                label="Deleted"
                color="error"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem', ml: 1 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {organizationUnit.parentOrganizationUnitId && (
              <IconButton
                size="small"
                onClick={onEdit}
                title="Edit"
                sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <Pencil size={16} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={onAdd}
              title="Add child"
              sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <Plus size={16} />
            </IconButton>
            {organizationUnit.parentOrganizationUnitId && (
              <IconButton
                size="small"
                color="error"
                onClick={onDelete}
                title="Delete"
                sx={{ p: 0.5 }}
              >
                <Trash2 size={16} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Paper>

      {isExpanded && organizationUnit.children?.length > 0 && (
        <Box sx={{ ml: 3, pl: 1.5, borderLeft: '1px solid', borderColor: 'divider' }}>
          {organizationUnit.children.map((child) => (
            <TreeStructure
              key={child.id}
              organizationUnit={child}
              onDragStart={onDragStart}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              onAddClick={onAddClick}
              dragTarget={dragTarget}
            />
          ))}
        </Box>
      )}

      <Dialog
        title="Are you sure you want to delete this unit?"
        handleApply={confirmDelete}
        confirmButtonLable="Yes, Delete"
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        cancelButtonLable="Cancel"
        dialogType={DIALOG_TYPE.ERROR}
        justify="end"
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Deleting this unit will also remove all its sub-units from the hierarchy.
        </Typography>
      </Dialog>
    </Box>
  );
};
